import React, { useEffect, useState } from 'react';
import { authToken } from '../../authToken';

import EventListAddPanel from './EventListAddPanel';
import EventListEditPanel from './EventListEditPanel';
import EventListItemMenuPanel from './EventListItemMenuPanel';
import { API_BASE_URL } from '../../config';

const API_EVENTLIST_URL = `${API_BASE_URL}/events`;

// This mirrors TaskListPanel ordering rules 1:1.
function sortEventListsCustom(lists) {
  const toMillis = (v) => {
    if (v === null || v === undefined) return Number.POSITIVE_INFINITY;
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  };

  // Compare "availability" against today's date at 00:00:00 UTC.
  const getTodayUtcMidnightMillis = () => {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
  };
  const todayUtcMidnight = getTodayUtcMidnightMillis();
  const isAvailableToDo = (event) => toMillis(event.validFrom) <= todayUtcMidnight;

  const collator = new Intl.Collator('pl', { sensitivity: 'base' });

  return [...lists].sort((a, b) => {
    const availA = isAvailableToDo(a);
    const availB = isAvailableToDo(b);
    if (availA !== availB) return availA ? -1 : 1;

    const vt = toMillis(a.validTo) - toMillis(b.validTo);
    if (vt !== 0) return vt;

    const nameCmp = collator.compare(a.name ?? '', b.name ?? '');
    if (nameCmp !== 0) return nameCmp;

    const vf = toMillis(a.validFrom) - toMillis(b.validFrom);
    if (vf !== 0) return vf;

    return (a.id ?? 0) - (b.id ?? 0);
  });
}

function getTodayUtcMidnightMillis() {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
}

function isAvailableToDo(event, todayUtcMidnight) {
  const vf = new Date(event.validFrom).getTime();
  return Number.isFinite(vf) && vf <= todayUtcMidnight;
}

function isOverdue(event, todayUtcMidnight) {
  const vt = new Date(event.validTo).getTime();
  return Number.isFinite(vt) && vt < todayUtcMidnight;
}

function isDueToday(event, todayUtcMidnight) {
  const vt = new Date(event.validTo).getTime();
  return Number.isFinite(vt) && vt === todayUtcMidnight;
}

function hasInvalidDateRange(event) {
  const vf = new Date(event.validFrom).getTime();
  const vt = new Date(event.validTo).getTime();
  return Number.isFinite(vf) && Number.isFinite(vt) && vf > vt;
}

function getEventColors(event, todayUtcMidnight) {
  if (hasInvalidDateRange(event)) {
    return { backgroundColor: '#FF0000', color: '#000000' };
  }

  // NOTE: Overdue events are filtered out of the list and should not be displayed.
  // Colors:
  // - active (available-to-do, including due today): orange/gold
  // - not available yet: violet
  if (isAvailableToDo(event, todayUtcMidnight)) {
    return { backgroundColor: '#fff6e6', color: '#8a5a00' };
  }

  return { backgroundColor: '#f0e6ff', color: '#4b2a7a' };
}

function EventListPanel({ onClose }) {
  const [eventLists, setEventLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addPanelInitialValues, setAddPanelInitialValues] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [menuIdx, setMenuIdx] = useState(null);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);

  const fetchLists = () => {
    setLoading(true);
    fetch(`${API_EVENTLIST_URL}/actual`, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch event lists');
        return res.json();
      })
      .then(data => {
        setEventLists(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };
  useEffect(() => { fetchLists(); }, []);

  const handleAdd = (event) => {
    fetch(API_EVENTLIST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify(event)
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add event');
        return res.json();
      })
      .then(() => {
        setShowAddPanel(false);
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleEdit = (id) => {
    const e = eventLists.find(x => x.id === id);
    if (!e) return;
    setEditEvent(e);
    setShowEditPanel(true);
    setMenuIdx(null);
  };

  const handleEditSave = (payload) => {
    fetch(`${API_EVENTLIST_URL}/${payload.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify({
        name: payload.name,
        validFrom: payload.validFrom,
        validTo: payload.validTo,
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to update event');
        return res.json();
      })
      .then(() => {
        setShowEditPanel(false);
        setEditEvent(null);
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleDelete = (id) => {
    setConfirmDeleteIdx(id);
    setMenuIdx(null);
  };

  const handleDuplicate = (id) => {
    const e = eventLists.find(x => x.id === id);
    if (!e) return;
    setMenuIdx(null);
    setAddPanelInitialValues({
      name: e.name,
      validFrom: e.validFrom,
      validTo: e.validTo,
    });
    setShowAddPanel(true);
  };

  const confirmDelete = (id) => {
    fetch(`${API_EVENTLIST_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete');
        setConfirmDeleteIdx(null);
        fetchLists();
      })
      .catch(err => {
        setError(err.message);
        setConfirmDeleteIdx(null);
      });
  };

  const todayUtcMidnight = getTodayUtcMidnightMillis();

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '420px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '650px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>
      <h2 style={{ marginBottom: 16 }}>Wydarzenia</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #6cf',
            background: '#eaf6ff',
            color: '#227',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onClick={() => {
            setAddPanelInitialValues(null);
            setShowAddPanel(true);
          }}
        >➕ Dodaj</button>
      </div>
      {loading && <div>Ładowanie...</div>}
      {error && <div style={{ color: 'red' }}>Błąd: {error}</div>}
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {sortEventListsCustom(
          eventLists.filter((ev) => hasInvalidDateRange(ev) || !isOverdue(ev, todayUtcMidnight))
        ).map((list) => {
          const colorStyle = getEventColors(list, todayUtcMidnight);
          return (
          <li
            key={list.id}
            style={{
              padding: '6px 0',
              borderBottom: '1px solid #eee',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              position: 'relative',
              userSelect: 'none',
              borderRadius: 10,
              paddingLeft: 10,
              paddingRight: 10,
              marginBottom: 3,
              ...(colorStyle ?? {}),
            }}
          >
            <span>
              <b>{list.name}</b> <span style={{color:'#aaa', fontSize:'0.95em'}}>({list.id})</span>
              <div style={{ marginTop: 0, lineHeight: 1.1 }}>
                <span style={{ color:'#888', fontSize:'0.82em' }}>Od: {new Date(list.validFrom).toISOString().slice(0,10)}</span>
                <span style={{ marginLeft:8, color:'#888', fontSize:'0.82em' }}>Do: {new Date(list.validTo).toISOString().slice(0,10)}</span>
              </div>
            </span>
            <button
              style={{
                background: 'none',
                border: 'none',
                fontSize: 22,
                cursor: 'pointer',
                padding: '0 8px',
                color: '#888',
                marginLeft: 8
              }}
              aria-label="Menu"
              onClick={e => {
                e.stopPropagation();
                setMenuIdx(list.id);
              }}
            >
              ⋯
            </button>
            {menuIdx === list.id && (
              <EventListItemMenuPanel
                onEdit={() => handleEdit(list.id)}
                onDuplicate={() => handleDuplicate(list.id)}
                onDelete={() => handleDelete(list.id)}
                onCancel={() => setMenuIdx(null)}
              />
            )}
            {confirmDeleteIdx === list.id && (
              <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                padding: 28,
                zIndex: 13000,
                minWidth: 240,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16
              }}>
                <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>Usunąć wydarzenie <b>{list.name}</b>?</div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <button
                    onClick={() => confirmDelete(list.id)}
                    style={{
                      padding: '10px 24px',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 8,
                      border: '1px solid #f66',
                      background: '#fff0f0',
                      color: '#a22',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >Usuń</button>
                  <button
                    onClick={() => setConfirmDeleteIdx(null)}
                    style={{
                      padding: '10px 24px',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      borderRadius: 8,
                      border: '1px solid #bbb',
                      background: '#f8f8f8',
                      color: '#555',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >Anuluj</button>
                </div>
              </div>
            )}
          </li>
          );
        })}
      </ul>
      {!loading && eventLists.length === 0 && <div>Brak wydarzeń :0</div>}
      {showAddPanel && (
        <EventListAddPanel
          onAdd={handleAdd}
          onCancel={() => {
            setShowAddPanel(false);
            setAddPanelInitialValues(null);
          }}
          initialValues={addPanelInitialValues}
        />
      )}

      {showEditPanel && editEvent && (
        <EventListEditPanel
          event={editEvent}
          onSave={handleEditSave}
          onCancel={() => {
            setShowEditPanel(false);
            setEditEvent(null);
          }}
        />
      )}
    </div>
  );
}

export default EventListPanel;
